import React from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Table from '@mui/joy/Table';
import Chip from '@mui/joy/Chip';
import Avatar from '@mui/joy/Avatar';
import CircularProgress from '@mui/joy/CircularProgress';
import Button from '@mui/joy/Button';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { getParserInfo, setParserState } from '../parserApi';
import { ParserInfo, ParserStateEnum } from '../../../models/parser';
import { gamesListApi } from '../../games/gamesApi';
import { Game } from '../../../models/games';
import moment from 'moment';
import { formatDateTime } from '../../../common/dateTime';
import ConfirmDialog from '../../../components/confirmDialog/ConfirmDialog';
import { NotificationService } from '../../../common/notification';

function stateToColor(state: ParserStateEnum): 'neutral' | 'primary' | 'success' | 'warning' {
    switch (state) {
        case ParserStateEnum.Init:
            return 'neutral';
        case ParserStateEnum.Run:
            return 'primary';
        case ParserStateEnum.Completed:
            return 'success';
        case ParserStateEnum.Skip:
            return 'warning';
        default:
            return 'neutral';
    }
}

function stateToLabel(state: ParserStateEnum): string {
    switch (state) {
        case ParserStateEnum.Init:
            return 'Init';
        case ParserStateEnum.Run:
            return 'Run';
        case ParserStateEnum.Completed:
            return 'Completed';
        case ParserStateEnum.Skip:
            return 'Skip';
        default:
            return String(state);
    }
}

export function ParserTab() {
    const [loading, setLoading] = React.useState<boolean>(true);
    const [data, setData] = React.useState<ParserInfo | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [games, setGames] = React.useState<Game[]>([]);
    const [confirmOpen, setConfirmOpen] = React.useState<boolean>(false);
    const [confirmText, setConfirmText] = React.useState<string>('');
    const [pendingToggle, setPendingToggle] = React.useState<{ appId: number, nextState: number } | null>(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getParserInfo(20);
            if (result.result && result.value) {
                setData(result.value as ParserInfo);
            } else {
                setError('Не удалось загрузить данные парсера');
            }
        } catch (e) {
            setError('Ошибка запроса к сервису парсера');
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const [parserResult, gamesResult] = await Promise.all([
                    getParserInfo(20),
                    gamesListApi(),
                ]);
                if (!mounted) return;
                if (parserResult.result && parserResult.value) {
                    setData(parserResult.value as ParserInfo);
                } else {
                    setError('Не удалось загрузить данные парсера');
                }
                if (gamesResult.result && gamesResult.value) {
                    setGames(gamesResult.value as Game[]);
                }
            } catch (e) {
                if (mounted) setError('Ошибка запроса к сервису парсера');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    const gamesMap = React.useMemo(() => {
        const map = new Map<number, Game>();
        games.forEach(g => map.set(g.id, g));
        return map;
    }, [games]);

    const getGameStrict = React.useCallback((id: number): Game | undefined => {
        const hit = gamesMap.get(id);
        if (hit) return hit;
        return games.find(g => Number(g.id) === Number(id));
    }, [gamesMap, games]);

    const getGameTitle = React.useCallback((id: number) => getGameStrict(id)?.name ?? id, [getGameStrict]);
    const getGameIcon = React.useCallback((id: number) => getGameStrict(id)?.icon ?? undefined, [getGameStrict]);

    const askToggle = React.useCallback((appId: number, currentState: ParserStateEnum) => {
        const isOn = currentState !== ParserStateEnum.Skip;
        const nextState = isOn ? ParserStateEnum.Skip : ParserStateEnum.Init; // 3 or 0
        setPendingToggle({ appId, nextState });
        setConfirmText(isOn ? 'Выключить парсер для этой игры?' : 'Включить парсер для этой игры?');
        setConfirmOpen(true);
    }, []);

    const confirmToggle = React.useCallback(async () => {
        if (!pendingToggle) return;
        const res = await setParserState(pendingToggle.appId, pendingToggle.nextState);
        setConfirmOpen(false);
        setPendingToggle(null);
        if (res.result) {
            NotificationService.alertSuccess('Готово');
            await load();
        } else {
            const msg = typeof res.value === 'string' ? res.value : 'Не удалось изменить состояние парсера';
            NotificationService.alertError(msg);
        }
    }, [pendingToggle, load]);

    const limitedHistory = React.useMemo(() => (data?.history || []).slice(0, 20), [data]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography level="h3">Состояние парсера</Typography>
                <Button size="sm" variant="outlined" startDecorator={<KeyboardArrowRightIcon/>} onClick={load} disabled={loading}>
                    Обновить
                </Button>
            </Box>

            {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size="sm" />
                    <Typography level="body-sm">Загрузка...</Typography>
                </Box>
            )}

            {!loading && error && (
                <Typography color="danger" level="body-md">{error}</Typography>
            )}

            {!loading && !error && data && (
                <>
                    <ConfirmDialog
                        title="Подтверждение"
                        open={confirmOpen}
                        setOpen={setConfirmOpen}
                        onConfirm={confirmToggle}
                    >
                        {confirmText}
                    </ConfirmDialog>
                    {/* Cards for state (responsive grid) */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                        {(data.state || []).map((s, idx) => (
                            <Sheet key={idx} variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar src={getGameIcon(s.appId)} alt={String(getGameTitle(s.appId))} size="sm" />
                                        <Typography level="title-sm">{getGameTitle(s.appId)}</Typography>
                                    </Box>
                                    <Chip variant="soft" color={stateToColor(s.state)} size="sm">{stateToLabel(s.state)}</Chip>
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                    <Typography level="body-xs" color="neutral">Старт</Typography>
                                    <Typography level="body-sm" textAlign="right">{formatDateTime(s.start)}</Typography>
                                    <Typography level="body-xs" color="neutral">Финиш</Typography>
                                    <Typography level="body-sm" textAlign="right">{formatDateTime(s.end)}</Typography>
                                    <Typography level="body-xs" color="neutral">Обработано</Typography>
                                    <Typography level="body-sm" textAlign="right">{s.skinCompletedCount ?? '-'}</Typography>
                                    <Typography level="body-xs" color="neutral">Всего</Typography>
                                    <Typography level="body-sm" textAlign="right">{s.total ?? '-'}</Typography>
                                    <Typography level="body-xs" color="neutral">Ошибок</Typography>
                                    <Typography level="body-sm" textAlign="right">{s.errorCount ?? '-'}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                                    {s.state !== ParserStateEnum.Skip ? (
                                        <Button size="sm" color="danger" variant="soft" onClick={() => askToggle(s.appId, s.state)}>
                                            Выкл
                                        </Button>
                                    ) : (
                                        <Button size="sm" color="success" variant="soft" onClick={() => askToggle(s.appId, s.state)}>
                                            Вкл
                                        </Button>
                                    )}
                                </Box>
                            </Sheet>
                        ))}
                    </Box>

                    <Typography level="h3" sx={{ mt: 2 }}>Последние запуски</Typography>

                    {/* Desktop table for history */}
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', display: { xs: 'none', sm: 'block' } }}>
                        <Table borderAxis="bothBetween" size="sm" sx={{ '--TableCell-headFontWeight': 600 }}>
                            <thead>
                                <tr>
                                    <th>Игра</th>
                                    <th>Старт</th>
                                    <th>Финиш</th>
                                    <th>Скинов</th>
                                    <th>Ошибок</th>
                                    <th>Длительность</th>
                                </tr>
                            </thead>
                            <tbody>
                                {limitedHistory.map((h, idx) => {
                                    const start = moment(h.start);
                                    const end = moment(h.end);
                                    const duration = end.isValid() && start.isValid() ? moment.utc(end.diff(start)).format('HH:mm:ss') : '-';
                                    return (
                                        <tr key={idx}>
                                            <td>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar src={getGameIcon(h.gameId)} alt={String(getGameTitle(h.gameId))} size="sm" />
                                                    <Typography level="body-sm">{getGameTitle(h.gameId)}</Typography>
                                                </Box>
                                            </td>
                                            <td>{formatDateTime(h.start)}</td>
                                            <td>{formatDateTime(h.end)}</td>
                                            <td>{h.skinCount}</td>
                                            <td>{h.errorCount}</td>
                                            <td>{duration}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Sheet>

                    {/* Mobile cards for history */}
                    <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                        {limitedHistory.map((h, idx) => {
                            const start = moment(h.start);
                            const end = moment(h.end);
                            const duration = end.isValid() && start.isValid() ? moment.utc(end.diff(start)).format('HH:mm:ss') : '-';
                            return (
                                <Sheet key={idx} variant="outlined" sx={{ p: 2, borderRadius: 'sm', mb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar src={getGameIcon(h.gameId)} alt={String(getGameTitle(h.gameId))} size="sm" />
                                            <Typography level="title-sm">{getGameTitle(h.gameId)}</Typography>
                                        </Box>
                                        <Chip size="sm" variant="soft">Скинов: {h.skinCount}</Chip>
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                        <Typography level="body-xs" color="neutral">Старт</Typography>
                                        <Typography level="body-sm" textAlign="right">{formatDateTime(h.start)}</Typography>
                                        <Typography level="body-xs" color="neutral">Финиш</Typography>
                                        <Typography level="body-sm" textAlign="right">{formatDateTime(h.end)}</Typography>
                                        <Typography level="body-xs" color="neutral">Ошибок</Typography>
                                        <Typography level="body-sm" textAlign="right">{h.errorCount}</Typography>
                                        <Typography level="body-xs" color="neutral">Длительность</Typography>
                                        <Typography level="body-sm" textAlign="right">{duration}</Typography>
                                    </Box>
                                </Sheet>
                            );
                        })}
                    </Box>
                </>
            )}
        </Box>
    );
}

export default ParserTab;


